
import { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { en } from '@/translations/en';
import { pl } from '@/translations/pl';
import { getLanguageFromLocation } from '@/utils/languageUtils';

interface LanguageContextType {
  language: string;
  setLanguage: (lang: string) => void;
  t: (key: string, options?: Record<string, any>) => string;
}

const translations = {
  en,
  pl,
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  // Initialize with the detected language
  const [language, setLanguage] = useState(getLanguageFromLocation());
  
  // Update HTML lang attribute when language changes
  useEffect(() => {
    document.documentElement.lang = language;
    localStorage.setItem('language', language);
  }, [language]);

  const t = (key: string, options?: Record<string, any>): string => {
    // Split the key by dots to access nested properties
    const keys = key.split('.');
    
    // Get the translation object for the current language
    const translationObj = translations[language as keyof typeof translations];
    
    // Traverse the nested objects to find the translation string
    let result: any = translationObj;
    for (const k of keys) {
      if (result && typeof result === 'object' && k in result) {
        result = result[k];
      } else {
        // If translation not found, return the key
        return key;
      }
    }
    
    // If the result is not a string, return the key
    if (typeof result !== 'string') {
      return key;
    }
    
    // Replace placeholders with values from options
    let text = result;
    if (options) {
      Object.keys(options).forEach(option => {
        text = text.replace(`{${option}}`, options[option]);
      });
    }
    
    return text;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
